import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '../shared/components/Toast';

type ToastType = 'success' | 'error';
type ShowToastFunction = (message: string, type?: ToastType) => void;

const ToastContext = createContext<ShowToastFunction | null>(null);

// Custom hook for use inside components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Global reference for use outside components
let showGlobalToast: ShowToastFunction = () => {};

// Function for use outside components
export const getShowToast = (): ShowToastFunction => showGlobalToast;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [toastType, setToastType] = useState<ToastType>('error');

  const showToast = useCallback((message: string, type: ToastType = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setIsVisible(true);
  }, []);

  // Update global reference
  showGlobalToast = showToast;

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {isVisible && toastMessage && (
        <Toast 
          message={toastMessage} 
          isVisible={isVisible}
          type={toastType}
          onClose={() => setIsVisible(false)}
        />
      )}
    </ToastContext.Provider>
  );
};